// @todo enable the following disabled rules see OPENTOK-31136 for more info
/* eslint-disable no-param-reassign */
import assign from 'lodash/assign';
import createLogger from '../../helpers/log';
import applySdpTransform from './applySdpTransform';
import handleHeaderExtensionsNegotiation from './handleHeaderExtensionsNegotiation';
import setAudioBitrate from './setAudioBitrate';
import { canNegotiateHeaderExtensions, canModifySenderParameters, getAudioTransceiversSenders } from './transceiverHelpers';

const logging = createLogger('subscribeProcessor');

// Attempt to completely process a subscribe message. This will:
// * create an Offer
// * set the new offer as the location description
//
// If there are no issues, the Promise will resolve on completion.
// Errors during any step will result in the Promise being rejected.
//
export default async function subscribeProcessor({
  peerConnection,
  NativeRTCSessionDescription,
  sdpTransforms,
  numSimulcastStreams,
  offerOverrides,
  offerConstraints,
  replaceBaselineProfile,
  removeVideoOrientation,
}) {
  const generateErrorCallback = (message, prefix) => (
    (errorReason) => {
      logging.error(message, errorReason);

      const error = new Error(message);
      error.reason = errorReason;
      error.prefix = prefix;
      throw error;
    }
  );

  const setLocalDescription = (offer) => {
    logging.debug('subscribeProcessor: offer created', offer);

    const transformOptions = assign(
      { numSimulcastStreams, replaceBaselineProfile, removeVideoOrientation }, offerOverrides
    );
    // Don't apply sdp mangling on properties if transceiver header extensions can be negotiated
    if (canNegotiateHeaderExtensions(peerConnection)) {
      delete transformOptions.removeVideoOrientation;
    }
    // Don't apply sdp mangling on audioBitrate if it can be set directly in the sender.
    if (canModifySenderParameters(peerConnection)) {
      delete transformOptions.audioBitrate;
    }

    const transformedSdp = applySdpTransform(
      sdpTransforms,
      'local',
      'offer',
      transformOptions,
      offer.sdp
    );

    const newLocalOffer = new NativeRTCSessionDescription({
      type: offer.type,
      sdp: transformedSdp.local,
    });

    const newRemoteOffer = new NativeRTCSessionDescription({
      type: offer.type,
      sdp: transformedSdp.remote,
    });

    logging.debug('subscribeProcessor: setting local description');

    return peerConnection
      .setLocalDescription(newLocalOffer)
      .then(
        () => newRemoteOffer,
        generateErrorCallback('Error while setting LocalDescription', 'SetLocalDescription')
      );
  };

  const preOfferOperations = async () => {
    if (canNegotiateHeaderExtensions(peerConnection)) {
      await handleHeaderExtensionsNegotiation(peerConnection, { removeVideoOrientation });
    }

    if (canModifySenderParameters(peerConnection) && offerOverrides?.audioBitrate > 0) {
      const audioSenders = getAudioTransceiversSenders(peerConnection);
      await setAudioBitrate(audioSenders, offerOverrides.audioBitrate);
    }
  };

  logging.debug('subscribeProcessor: creating offer');

  return preOfferOperations()
    .then(() => peerConnection.createOffer(offerConstraints))
    .then(setLocalDescription)
    .then(offer => ({
      sdp: offer.sdp,
      type: offer.type,
    }))
    .catch(generateErrorCallback('Error while creating Offer', 'CreateOffer'));
}
